import React, { useState } from 'react';

interface ForgotPasswordProps {
  isOpen: boolean;
  onClose: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setMessage('Please enter your email address.');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const response = await fetch('/api/sendgrid/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, action: 'request' }),
      });
      if (response.ok) {
        setIsSuccess(true);
        setMessage('Password reset email sent! Check your inbox.');
      } else {
        setMessage('Failed to send reset email. Please try again.');
      }
    } catch (err) {
      setMessage('Network error. Please try again later.');
    }
    setLoading(false);
  };

  const handleClose = () => {
    setEmail('');
    setMessage('');
    setIsSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="p-8 text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isSuccess ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
            <i className={`fas ${isSuccess ? 'fa-check' : 'fa-key'} text-2xl`}></i>
          </div>
          <h2 className="font-serif text-2xl font-bold text-slate-900 mb-2">
            {isSuccess ? 'Email Sent!' : 'Forgot Password?'}
          </h2>
          <p className="text-slate-500 text-sm">
            {isSuccess
              ? 'Check your inbox for the password reset link.'
              : 'Enter your email address and we will send you a link to reset your password.'}
          </p>
        </div>
        {!isSuccess && (
          <form onSubmit={handleSubmit} className="px-8 pb-8 space-y-4">
            {message && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm font-medium border border-red-100">
                {message}
              </div>
            )}
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                Office Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@lluislaw.com"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-600 outline-none transition-all font-medium"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white font-bold py-3 rounded-xl hover:bg-slate-800 transition-all text-sm uppercase tracking-widest"
            >
              {loading ? <i className="fas fa-circle-notch animate-spin"></i> : 'Send Reset Link'}
            </button>
          </form>
        )}
        <div className="px-8 pb-8">
          <button
            onClick={handleClose}
            className="w-full text-slate-500 font-medium text-sm hover:text-slate-700 transition-colors"
          >
            {isSuccess ? 'Close' : 'Back to Login'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
